import React from "react";
import styled from "styled-components";

import { MappingConfiguration, MappingRule } from "../../rule-engine-core/Mapping/MappingConfiguration";
import { ExpressionView } from "../../Expressions/ExpressionView";
import { ExpressionIcon } from "../../CommonUI/Icon/ExpressionIcon";
import { MappingEditContext } from "../Builder/MappingEditContext";

import { MappingConstants } from "../../rule-engine-core/Mapping/MappingConstants";

import { ExpressionValueType } from "../../rule-engine-core/Expressions/ExpressionValueType";
import { VariableIcon } from "../../CommonUI/Icon/VariableIcon";
import { ConstantIcon } from "../../CommonUI/Icon/ConstantIcon";
import { Popper } from "@mui/material";
import { AttentionIcon } from "../../CommonUI/Icon/AttentionIcon";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

const XMappingRuleExists = ({
    isInvalid,
    isDraggingOver,
    size,
    mappingRule,
    definition,
    editContext,
}: {
    isInvalid?: boolean;
    isDraggingOver: boolean;
    size: "Small" | "Medium";
    mappingRule: MappingRule;
    definition: MappingConfiguration;
    editContext: MappingEditContext;
}) => {
    const [expressionViewEl, setExpresssionViewEl] = React.useState<HTMLElement | null>(null);
    const [openExp, setOpenExp] = React.useState<boolean>(false);

    const setRef = (ref: HTMLDivElement | null) => {
        setExpresssionViewEl(ref);
    };

    let icon: JSX.Element = <VariableIcon />;

    if (mappingRule.mapping.valueType === ExpressionValueType.VALUE) {
        icon = <ConstantIcon />;
    } else if (mappingRule.mapping.valueType === ExpressionValueType.SUBEXPRESSION) {
        icon = <ExpressionIcon />;
    }

    let forSourceId = editContext.ruleIdForSource(mappingRule.id);
    let forDestinationId = editContext.ruleIdForDestination(mappingRule.id);

    return (
        <>
            {mappingRule.getValidationIssue(definition.getExecutionResult()).length > 0 && (
                <MappingRuleError>
                    <AttentionIcon />
                </MappingRuleError>
            )}
            <XMappingRuleExpressionRender
                size={size}
                isDraggingOver={isDraggingOver}
                isInvalid={isInvalid}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    event.stopPropagation();
                    setOpenExp(true);
                }}
                isHighlighted={editContext.isHighlighted(mappingRule.id)}
            >
                {icon}
            </XMappingRuleExpressionRender>

            <XMappingConnectorForSource id={forSourceId} />
            <XMappingConnectorForDestination id={forDestinationId} />

            <ElementForPopper ref={setRef} />

            {/* {mappingRule.mapping.valueType !== ExpressionValueType.SUBEXPRESSION &&
                mappingRule.mapping.valueType !== ExpressionValueType.VALUE && (
                    <DragConnector draggableId={mappingRule.id} direction="left" icon={<OpenReverseIcon />} />
                )} */}

            {editContext.hoveredExpression !== mappingRule.id && (
                <XExpressionRender open={false}>
                    <ExpressionView
                        expression={mappingRule.mapping}
                        hideValidation
                        onDone={() => {
                            StateManager.changed(definition);
                        }}
                        open={openExp}
                        color={{
                            result: UIStyleConstants.BLUE_COLOR,
                            expression: UIStyleConstants.BLUE_COLOR,
                            variable: "" + UIStyleConstants.GRAY_COLOR,
                            value: UIStyleConstants.BLACK_COLOR,
                        }}
                        showClear={true}
                        clearName={"Delete Mapping"}
                    />
                </XExpressionRender>
            )}

            <StyledPopper
                open={editContext.hoveredExpression === mappingRule.id}
                anchorEl={expressionViewEl}
                placement="left-start"
                transition={false}
            >
                <XExpressionRender
                    className="ExpressionView"
                    onClick={() => setOpenExp(true)}
                    open={editContext.hoveredExpression === mappingRule.id}
                >
                    <ExpressionView
                        expression={mappingRule.mapping}
                        hideValidation
                        showClear={true}
                        clearName={"Delete Mapping"}
                        onDone={() => {
                            StateManager.changed(definition);
                        }}
                        open={openExp}
                        color={{
                            result: UIStyleConstants.BLUE_COLOR,
                            expression: UIStyleConstants.BLUE_COLOR,
                            variable: "" + UIStyleConstants.GRAY_COLOR,
                            value: UIStyleConstants.BLACK_COLOR,
                        }}
                    />
                </XExpressionRender>
            </StyledPopper>
        </>
    );
};

const XMappingRuleExpression = ({
    mappingRule,
    size,
    isDraggingOver,
    isInvalid,
    definition,
    editContext,
}: {
    mappingRule: MappingRule;
    definition: MappingConfiguration;
    editContext: MappingEditContext;
    size: "Small" | "Medium";
    isDraggingOver: boolean;
    isInvalid?: boolean;
}) => {
    const [openExp, setOpenExp] = React.useState<boolean>(false);

    if (mappingRule.empty) {
        return (
            <EmptyExpression className="EmptyRule-root">
                <XMappingRuleExpressionRender
                    size={size}
                    isDraggingOver={isDraggingOver}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                        event.stopPropagation();
                        mappingRule.mapping.valueType = ExpressionValueType.SUBEXPRESSION;
                        setOpenExp(true);
                    }}
                >
                    <ExpressionIcon />
                </XMappingRuleExpressionRender>
                <XMappingRuleExpressionRender
                    size={size}
                    isDraggingOver={isDraggingOver}
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                        event.stopPropagation();
                        mappingRule.mapping.valueType = ExpressionValueType.VALUE;
                        setOpenExp(true);
                    }}
                >
                    <ConstantIcon />
                </XMappingRuleExpressionRender>
                <ExpressionView
                    expression={mappingRule.mapping}
                    hideValidation
                    onDone={() => {
                        StateManager.changed(definition);
                    }}
                    open={openExp}
                    color={{
                        result: UIStyleConstants.BLUE_COLOR,
                        expression: UIStyleConstants.BLUE_COLOR,
                        variable: "" + UIStyleConstants.GRAY_COLOR,
                        value: UIStyleConstants.BLACK_COLOR,
                    }}
                />
            </EmptyExpression>
        );
    }
    return (
        <XMappingRuleExists
            size={size}
            isDraggingOver={isDraggingOver}
            isInvalid={isInvalid}
            mappingRule={mappingRule}
            definition={definition}
            editContext={editContext}
        />
    );
};

export interface IMappingRule {
    mappingRule: MappingRule;
    editContext: MappingEditContext;
    definition: MappingConfiguration;
    isInvalid?: boolean;
}

export const XMappingRule = (props: IMappingRule) => {
    const { mappingRule, definition, editContext } = props;

    let forSourceId = `${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${mappingRule.targetPath}_connector_forsource`;
    let forDestinationId = `${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${mappingRule.targetPath}_connector_fordestination`;

    let isCollapsed = props.editContext.isDestinationCollapsed(mappingRule.targetPath);

    if (isCollapsed) {
        return (
            <>
                <XMappingConnectorForSource id={forSourceId} />
                <XMappingConnectorForDestination id={forDestinationId} />
            </>
        );
    }

    return (
        <XMappingRuleRender
            isInvalid={props.isInvalid}
            onMouseEnter={() => {
                editContext.hoveredExpression = mappingRule.id;
            }}
            onMouseLeave={() => {
                editContext.hoveredExpression = undefined;
            }}
            isDraggingOver
        >
            <XMappingRuleExpression
                mappingRule={mappingRule}
                editContext={props.editContext}
                definition={definition}
                size={mappingRule.targetPath.includes(".") ? "Small" : "Medium"}
                isInvalid={props.isInvalid}
                isDraggingOver={false}
            />
        </XMappingRuleRender>
    );
};

const XMappingRuleRender = styled.div<{ isDraggingOver: boolean; isInvalid?: boolean; isHidden?: boolean }>`
    box-sizing: border-box;
    position: relative;

    .drag-connector {
        opacity: 0;
    }

    .drag-connector.isdragging {
        opacity: 1;
    }

    cursor: pointer;

    ${(props) =>
        !props.isInvalid &&
        !props.isDraggingOver &&
        `
            &:hover > .drag-connector {
                opacity: 1;
            }
        `}
`;

const XMappingRuleExpressionRender = styled.div<{
    size: "Small" | "Medium";
    isDraggingOver: boolean;
    isInvalid?: boolean;
    isHighlighted?: boolean;
}>`
    ${(props) =>
        props.isInvalid
            ? ""
            : props.isDraggingOver
              ? "background: ${UIStyleConstants.BLUE_LIGHT_COLOR};"
              : "background: " + UIStyleConstants.WHITE_COLOR}

    display: flex;

    border: 1px solid
        ${(props) =>
            props.isInvalid
                ? "" + UIStyleConstants.GRAY_LIGHTER_COLOR
                : props.isHighlighted
                  ? MappingConstants.HIGHLIGHT_MAPPING_LINE_COLOR
                  : UIStyleConstants.BLUE_COLOR};
    box-sizing: border-box;
    border-radius: 4px;
    ${(props) =>
        props.size === "Small"
            ? `
                width: 28px;
                height: 28px;
                padding: 5px;

                img {
                    width: 16px;
                    height: 16px;
                }
            `
            : `
                width: 40px;
                height: 40px;
                padding: 9px;

                img {
                    width: 20px;
                    height: 20px;
                }
            `}
`;

const EmptyExpression = styled.div`
    display: flex;
    align-items: center;

    opacity: 0;
    transition: opacity 0.1s ease-in;

    .ExpressionView-root {
        display: none;
    }
`;

const XMappingConnectorForSource = styled.div`
    position: absolute;
    top: 50%;
    left: 0;
`;

const XMappingConnectorForDestination = styled.div`
    position: absolute;
    top: 50%;
    right: 0px;
`;

const XExpressionRender = styled.div<{ open: boolean }>`
    position: relative;

    background: ${UIStyleConstants.WHITE_COLOR};

    box-sizing: border-box;

    transition: all 0.1s ease-in;

    z-index: 1;

    ${(props) =>
        !props.open
            ? `
                height: 0;
                width: 0;
                overflow: hidden;
            `
            : `
                border: 2px solid ${MappingConstants.HIGHLIGHT_MAPPING_LINE_COLOR};
                border-radius: 8px;
                padding: 2px;

            `}
`;

const StyledPopper = styled(Popper)`
    .ExpressionView-root {
        border: 1px solid ${UIStyleConstants.BLUE_COLOR};
        box-sizing: border-box;
        box-shadow: 0px 4px 15px -2px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 4px;
    }
`;

const ElementForPopper = styled.div`
    position: absolute;
    left: 0;
    top: calc(50% + 5px);
`;

const MappingRuleError = styled.div`
    position: absolute;
    top: -5px;
    right: 5px;
`;
